import { FC, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { Bar, Cell, ResponsiveContainer, BarChart, XAxis } from 'recharts';

import { StatisticDetailItem } from 'src/models/pages/profession';

import CustomBarShape from 'src/pages/Profession/components/SectionStatistics/DetailChart/Chart/CustomBarShape';
import {
    CHART_HEIGHT,
    BAR_MIN_WIDTH,
    BORDER_RADIUS,
    CHART_MARGIN,
    XAXIS_HEIGHT,
    BAR_MIN_HEIGHT,
    ANIMATION_DURATION,
} from 'src/pages/Profession/components/SectionStatistics/DetailChart/Chart/constants';
import {
    getMinWidth,
    tickFormatter,
} from 'src/pages/Profession/components/SectionStatistics/DetailChart/Chart/helpers';

import styles from './styles.less';

interface Props {
    data: StatisticDetailItem[];
    selectedItem: StatisticDetailItem;
    onSelect: (value: StatisticDetailItem) => void;
}

const Chart: FC<Props> = ({ data, selectedItem, onSelect }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (containerRef.current) {
                const activeBar = containerRef.current.querySelector(`.${styles.cell_active}`);
                if (activeBar) {
                    containerRef.current.scrollTo({
                        behavior: 'smooth',
                        left: activeBar.getBoundingClientRect().left - containerRef.current.clientWidth / 2 + 20,
                    });
                }
            }
        }, ANIMATION_DURATION);

        return () => clearTimeout(timeout);
    }, []);

    return (
        <div className={styles.container} ref={containerRef}>
            <ResponsiveContainer width="100%" height={CHART_HEIGHT} minWidth={getMinWidth(data.length)}>
                <BarChart data={data} margin={CHART_MARGIN}>
                    <XAxis
                        dataKey="salaryStr"
                        tickFormatter={tickFormatter}
                        height={XAXIS_HEIGHT}
                        interval={0}
                        axisLine={false}
                        tickLine={false}
                    />
                    <Bar
                        dataKey="count"
                        onClick={onSelect}
                        minPointSize={BAR_MIN_HEIGHT}
                        barSize={BAR_MIN_WIDTH}
                        shape={(props) => <CustomBarShape {...props} />}
                        animationDuration={ANIMATION_DURATION}
                        animationEasing="ease-out"
                    >
                        {data.map((item) => (
                            <Cell
                                key={`cell-${item.id}`}
                                radius={BORDER_RADIUS}
                                className={classnames(styles.cell, {
                                    [styles.cell_active]: selectedItem.id === item.id,
                                    [styles.cell_disabled]: selectedItem.id === item.id || !item.count,
                                })}
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default Chart;
